export const PUBLIC_MENU_FONT_FAMILIES = [
  `Inter`,
  "Roboto",
  "Open Sans",
  "Montserrat",
  "Poppins",
  "Nunito",
  "Lato",
  "Merriweather",
  "Playfair Display",
  "Oswald",
  "Bebas Neue",
  "Cabinet Grotesk",
  "Cinzel",
  "Cormorant Garamond",
  "DM Sans",
  "Lora",
  "Plus Jakarta Sans",
  "Quicksand",
  "Space Grotesk",
] as const;

export const PUBLIC_MENU_FONT_WEIGHTS = [400, 500, 600, 700] as const;
export const PUBLIC_MENU_PRESET_IDS = [
  "modern-light",
  "elegant-dark",
  "warm-restaurant",
  "minimal",
  "vibrant-brand",
  "crimson-red",
  "organic-green",
  "golden-yellow",
  "citrus-orange",
  "bold-burger-red",
  "neon-night",
  "espresso-vintage",
  "caribbean-breeze",
  "velvet-luxury",
  "dinner-king",
  "wingers-diner",
] as const;
export const PUBLIC_MENU_LAYOUT_IDS = ["default", "wingers", "dinnerking"] as const;
export type PublicMenuFontFamily = (typeof PUBLIC_MENU_FONT_FAMILIES)[number];
export type PublicMenuFontWeight = (typeof PUBLIC_MENU_FONT_WEIGHTS)[number];
export type PublicMenuPresetId = (typeof PUBLIC_MENU_PRESET_IDS)[number];
export type PublicMenuLayoutId = (typeof PUBLIC_MENU_LAYOUT_IDS)[number];
export type PublicMenuAppearanceMode = "light" | "dark" | "custom";
export type PublicMenuRadius = "small" | "medium" | "large";
export type PublicMenuCardStyle = "flat" | "bordered" | "elevated";
export type PublicMenuButtonStyle = "solid" | "soft" | "outline";
export type PublicMenuDensity = "compact" | "comfortable";
export type PublicMenuTypographyScale = "compact" | "normal" | "large";
export type PublicMenuNavbarStyle = "clear" | "primary" | "dark";
export type PublicMenuPanelStyle = "surface" | "surfaceAlt";
export type PublicMenuChatStyle = "surface" | "surfaceAlt" | "tinted";
export type PublicMenuLayoutConfig = Record<string, never>;

export interface PublicMenuTheme {
  version: 1;
  presetId: PublicMenuPresetId;
  layoutId: PublicMenuLayoutId;
  layoutConfig: PublicMenuLayoutConfig;
  appearanceMode: PublicMenuAppearanceMode;
  colors: {
    background: string;
    surface: string;
    surfaceAlt: string;
    primary: string;
    primaryForeground: string;
    secondary: string;
    secondaryForeground: string;
    accent: string;
    accentForeground: string;
    textPrimary: string;
    textSecondary: string;
    textMuted: string;
    border: string;
    price: string;
    promo: string;
    success: string;
    danger: string;
    warning: string;
  };
  typography: {
    headingFont: PublicMenuFontFamily;
    bodyFont: PublicMenuFontFamily;
    headingWeight: PublicMenuFontWeight;
    bodyWeight: PublicMenuFontWeight;
    scale: PublicMenuTypographyScale;
  };
  appearance: {
    borderRadius: PublicMenuRadius;
    cardStyle: PublicMenuCardStyle;
    buttonStyle: PublicMenuButtonStyle;
    density: PublicMenuDensity;
    navbarStyle: PublicMenuNavbarStyle;
    cartStyle: PublicMenuPanelStyle;
    chatStyle: PublicMenuChatStyle;
    configuratorStyle: PublicMenuPanelStyle;
  };
}

export interface PublicMenuThemePreset {
  id: PublicMenuPresetId;
  name: string;
  description: string;
  theme: PublicMenuTheme;
}

export interface LegacyMenuColors {
  primary: string;
  secondary: string;
  accent: string;
}

export const DEFAULT_PUBLIC_THEME: PublicMenuTheme = {
  version: 1,
  presetId: "modern-light",
  layoutId: "default",
  layoutConfig: {},
  appearanceMode: "light",
  colors: {
    background: "#F8FAFC",
    surface: "#FFFFFF",
    surfaceAlt: "#F1F5F9",
    primary: "#111827",
    primaryForeground: "#FFFFFF",
    secondary: "#E2E8F0",
    secondaryForeground: "#111827",
    accent: "#F97316",
    accentForeground: "#111827",
    textPrimary: "#111827",
    textSecondary: "#475569",
    textMuted: "#5B677A",
    border: "#E2E8F0",
    price: "#111827",
    promo: "#E11D48",
    success: "#15803D",
    danger: "#B91C1C",
    warning: "#B45309",
  },
  typography: {
    headingFont: "Inter",
    bodyFont: "Inter",
    headingWeight: 700,
    bodyWeight: 400,
    scale: "normal",
  },
  appearance: {
    borderRadius: "medium",
    cardStyle: "bordered",
    buttonStyle: "solid",
    density: "comfortable",
    navbarStyle: "clear",
    cartStyle: "surface",
    chatStyle: "surface",
    configuratorStyle: "surface",
  },
};

const HEX_COLOR = /^#[0-9A-F]{6}$/;

function createPreset(
  id: PublicMenuPresetId,
  name: string,
  description: string,
  overrides: {
    appearanceMode?: PublicMenuAppearanceMode;
    colors: Partial<PublicMenuTheme["colors"]>;
    typography?: Partial<PublicMenuTheme["typography"]>;
    appearance?: Partial<PublicMenuTheme["appearance"]>;
  },
): PublicMenuThemePreset {
  const colors = { ...DEFAULT_PUBLIC_THEME.colors, ...overrides.colors };
  colors.primaryForeground =
    overrides.colors.primaryForeground ?? getReadableForeground(colors.primary);
  colors.secondaryForeground =
    overrides.colors.secondaryForeground ??
    getReadableForeground(colors.secondary);
  colors.accentForeground =
    overrides.colors.accentForeground ?? getReadableForeground(colors.accent);
  return {
    id,
    name,
    description,
    theme: {
      version: 1,
      presetId: id,
      layoutId: DEFAULT_PUBLIC_THEME.layoutId,
      layoutConfig: DEFAULT_PUBLIC_THEME.layoutConfig,
      appearanceMode:
        overrides.appearanceMode ?? DEFAULT_PUBLIC_THEME.appearanceMode,
      colors,
      typography: {
        ...DEFAULT_PUBLIC_THEME.typography,
        ...overrides.typography,
      },
      appearance: {
        ...DEFAULT_PUBLIC_THEME.appearance,
        ...overrides.appearance,
      },
    },
  };
}

export const PUBLIC_MENU_PRESETS: readonly PublicMenuThemePreset[] = [
  createPreset("wingers-diner", "WINGER'S Diner", "Rojo, naranja y crema; tipografía de carta diner.", {
    colors: {
      background: "#FFF9F2", surface: "#FFFFFF", surfaceAlt: "#FFF0DE",
      primary: "#CD2026", primaryForeground: "#FFFFFF",
      secondary: "#221915", secondaryForeground: "#FFFFFF",
      accent: "#F09130", accentForeground: "#221915",
      textPrimary: "#221915", textSecondary: "#62554C", textMuted: "#78675B",
      border: "#E8D9CC", price: "#B71C23", promo: "#F09130",
      success: "#17653D", danger: "#B71C23", warning: "#92400E",
    },
    typography: { headingFont: "Oswald", bodyFont: "Inter", headingWeight: 700, bodyWeight: 400 },
    appearance: { borderRadius: "small", cardStyle: "bordered", buttonStyle: "solid", density: "comfortable", navbarStyle: "clear" },
  }),
  createPreset(
    "modern-light",
    "Claro moderno",
    "Limpio, luminoso y fácil de leer.",
    {
      colors: {},
    },
  ),
  createPreset(
    "elegant-dark",
    "Oscuro elegante",
    "Superficies profundas con contraste cálido.",
    {
      appearanceMode: "dark",
      colors: {
        background: "#0B0F14",
        surface: "#151A21",
        surfaceAlt: "#202731",
        primary: "#F4C95D",
        accent: "#F97316",
        textPrimary: "#F8FAFC",
        textSecondary: "#CBD5E1",
        textMuted: "#94A3B8",
        border: "#334155",
        price: "#F4C95D",
        promo: "#FB7185",
        success: "#4ADE80",
        danger: "#FB7185",
        warning: "#FBBF24",
      },
      typography: {
        headingFont: "Playfair Display",
        bodyFont: "Inter",
        headingWeight: 700,
      },
      appearance: {
        cardStyle: "elevated",
        navbarStyle: "dark",
        borderRadius: "large",
      },
    },
  ),
  createPreset(
    "warm-restaurant",
    "Cálido restaurante",
    "Tonos crema y terracota para una carta cercana.",
    {
      colors: {
        background: "#FFF7ED",
        surface: "#FFFBF5",
        surfaceAlt: "#FDEAD7",
        primary: "#9A3412",
        secondary: "#FED7AA",
        accent: "#EA580C",
        textPrimary: "#431407",
        textSecondary: "#7C2D12",
        textMuted: "#9A5B43",
        border: "#F3C7A8",
        price: "#C2410C",
        promo: "#BE123C",
      },
      typography: {
        headingFont: "Merriweather",
        bodyFont: "Nunito",
        headingWeight: 700,
      },
      appearance: {
        cardStyle: "bordered",
        navbarStyle: "primary",
        borderRadius: "large",
      },
    },
  ),
  createPreset(
    "minimal",
    "Minimalista",
    "Jerarquía sobria con pocos contrastes de color.",
    {
      colors: {
        background: "#FFFFFF",
        surface: "#FFFFFF",
        surfaceAlt: "#F7F7F7",
        primary: "#18181B",
        secondary: "#E4E4E7",
        accent: "#52525B",
        textPrimary: "#18181B",
        textSecondary: "#52525B",
        textMuted: "#71717A",
        border: "#E4E4E7",
        price: "#27272A",
        promo: "#BE123C",
      },
      typography: {
        headingFont: "Inter",
        bodyFont: "Inter",
        headingWeight: 600,
      },
      appearance: {
        cardStyle: "flat",
        buttonStyle: "outline",
        borderRadius: "small",
        density: "compact",
      },
    },
  ),
  createPreset(
    "vibrant-brand",
    "Marca vibrante",
    "Color fuerte, tarjetas suaves y CTAs protagonistas.",
    {
      colors: {
        background: "#F5F3FF",
        surface: "#FFFFFF",
        surfaceAlt: "#EDE9FE",
        primary: "#6D28D9",
        secondary: "#C4B5FD",
        accent: "#F59E0B",
        textPrimary: "#2E1065",
        textSecondary: "#5B21B6",
        textMuted: "#6D5E95",
        border: "#DDD6FE",
        price: "#6D28D9",
        promo: "#DB2777",
      },
      typography: {
        headingFont: "Poppins",
        bodyFont: "Open Sans",
        headingWeight: 700,
      },
      appearance: {
        cardStyle: "elevated",
        buttonStyle: "solid",
        navbarStyle: "primary",
        borderRadius: "large",
      },
    },
  ),
// --- Temas por Color ---
  createPreset('crimson-red', 'Rojo carmesí', 'Elegante y tentador, con gran impacto visual.', {
    colors: {
      background: '#FEF2F2', surface: '#FFFFFF', surfaceAlt: '#FEE2E2', primary: '#991B1B',
      secondary: '#FECACA', accent: '#DC2626', textPrimary: '#450A0A', textSecondary: '#7F1D1D',
      textMuted: '#991B1B', border: '#FCA5A5', price: '#B91C1C', promo: '#E11D48',
    },
    typography: { headingFont: 'Playfair Display', bodyFont: 'Lato', headingWeight: 700 },
    appearance: { cardStyle: 'elevated', navbarStyle: 'primary', borderRadius: 'medium' },
  }),

  createPreset('organic-green', 'Verde botánico', 'Fresco y natural, ideal para cartas saludables o veganas.', {
    colors: {
      background: '#F0FDF4', surface: '#FFFFFF', surfaceAlt: '#DCFCE7', primary: '#15803D',
      secondary: '#BBF7D0', accent: '#16A34A', textPrimary: '#052E16', textSecondary: '#14532D',
      textMuted: '#166534', border: '#86EFAC', price: '#15803D', promo: '#E11D48',
    },
    typography: { headingFont: 'Lora', bodyFont: 'Plus Jakarta Sans', headingWeight: 600 },
    appearance: { cardStyle: 'bordered', navbarStyle: 'primary', borderRadius: 'large' },
  }),

  createPreset('golden-yellow', 'Amarillo cálido', 'Luminoso y accesible, enfocado en desayunos y cafetería.', {
    colors: {
      background: '#FFFBEB', surface: '#FFFFFF', surfaceAlt: '#FEF3C7', primary: '#B45309',
      secondary: '#FDE68A', accent: '#F59E0B', textPrimary: '#451A03', textSecondary: '#78350F',
      textMuted: '#92400E', border: '#FDE68A', price: '#B45309', promo: '#DC2626',
    },
    typography: { headingFont: 'Quicksand', bodyFont: 'Inter', headingWeight: 700 },
    appearance: { cardStyle: 'elevated', navbarStyle: 'primary', borderRadius: 'large' },
  }),

  createPreset('citrus-orange', 'Naranja cítrico', 'Vibrante y apetitoso, pensado para fast food y comida urbana.', {
    colors: {
      background: '#FFF7ED', surface: '#FFFFFF', surfaceAlt: '#FFEDD5', primary: '#C2410C',
      secondary: '#FED7AA', accent: '#F97316', textPrimary: '#431407', textSecondary: '#7C2D12',
      textMuted: '#9A3412', border: '#FDBA74', price: '#EA580C', promo: '#BE123C',
    },
    typography: { headingFont: 'Montserrat', bodyFont: 'Roboto', headingWeight: 700 },
    appearance: { cardStyle: 'bordered', navbarStyle: 'primary', borderRadius: 'medium' },
  }),

  createPreset('bold-burger-red', 'Burger rojo intenso', 'Rojo protagonista, gris carbón y blanco limpio para marcas urbanas de burger.', {
    appearanceMode: 'dark',
    colors: {
      background: '#111111', surface: '#18181B', surfaceAlt: '#27272A', primary: '#DC2626',
      primaryForeground: '#FFFFFF', secondary: '#3F3F46', secondaryForeground: '#FFFFFF', accent: '#FFFFFF',
      accentForeground: '#111111', textPrimary: '#FFFFFF', textSecondary: '#E4E4E7', textMuted: '#A1A1AA',
      border: '#3F3F46', price: '#FFFFFF', promo: '#EF4444', success: '#22C55E', danger: '#EF4444',
      warning: '#F59E0B',
    },
    typography: { headingFont: 'Bebas Neue', bodyFont: 'Inter', headingWeight: 700, bodyWeight: 500 },
    appearance: { cardStyle: 'elevated', buttonStyle: 'solid', navbarStyle: 'dark', borderRadius: 'medium' },
  }),

  // --- Temas Creativos / Concepto ---
  createPreset('neon-night', 'Noche neón', 'Estilo cyber-bar urbano con acentos cian y violeta para coctelerías.', {
    appearanceMode: 'dark',
    colors: {
      background: '#0B0F19', surface: '#111827', surfaceAlt: '#1F2937', primary: '#06B6D4',
      secondary: '#374151', accent: '#F43F5E', textPrimary: '#F9FAFB', textSecondary: '#9CA3AF',
      textMuted: '#A3AEC0', border: '#374151', price: '#22D3EE', promo: '#F43F5E',
    },
    typography: { headingFont: 'Space Grotesk', bodyFont: 'Plus Jakarta Sans', headingWeight: 700 },
    appearance: { cardStyle: 'elevated', navbarStyle: 'dark', borderRadius: 'medium' },
  }),

  createPreset('espresso-vintage', 'Café & Bistro', 'Cálido y tostado en tonos grano de café y crema para bocaterías y autor.', {
    colors: {
      background: '#FAF7F2', surface: '#FFFFFF', surfaceAlt: '#F3ECE2', primary: '#4A2E2B',
      secondary: '#D7C4B7', accent: '#B48265', textPrimary: '#2C1A18', textSecondary: '#5C403C',
      textMuted: '#7A5D58', border: '#E4D5C7', price: '#8B4513', promo: '#9B2C2C',
    },
    typography: { headingFont: 'Cinzel', bodyFont: 'Lora', headingWeight: 700 },
    appearance: { cardStyle: 'bordered', navbarStyle: 'primary', borderRadius: 'small' },
  }),

  createPreset('caribbean-breeze', 'Brisa marina', 'Turquesa acuoso y coral pensado para marisquerías y chiringuitos de playa.', {
    colors: {
      background: '#F0FDFA', surface: '#FFFFFF', surfaceAlt: '#CCFBF1', primary: '#0D9488',
      secondary: '#99F6E4', accent: '#FB7185', textPrimary: '#042F2E', textSecondary: '#115E59',
      textMuted: '#134E4A', border: '#5EEAD4', price: '#0F766E', promo: '#F43F5E',
    },
    typography: { headingFont: 'Cabinet Grotesk', bodyFont: 'DM Sans', headingWeight: 700 },
    appearance: { cardStyle: 'elevated', navbarStyle: 'primary', borderRadius: 'large' },
  }),
createPreset(
  'dinner-king',
  'Dinner King',
  'Identidad urbana y contundente basada en amarillo dorado, negro y rojo, inspirada en la estética original de Dinner King.',
  {
    appearanceMode: 'light',

    colors: {
      // Fondos
      background: '#FDB515',
      surface: '#FFF8E7',
      surfaceAlt: '#FDE68A',

      // Marca
      primary: '#D90F16',
      primaryForeground: '#FFFFFF',

      secondary: '#111111',
      secondaryForeground: '#FFFFFF',

      accent: '#FDB515',
      accentForeground: '#111111',

      // Texto
      textPrimary: '#111111',
      textSecondary: '#3F3F3F',
      textMuted: '#5A3A00',

      // UI
      border: '#1A1A1A',

      // Precios y estados
      price: '#111111',
      promo: '#D90F16',
      success: '#15803D',
      danger: '#DC2626',
      warning: '#D97706',
    },

    typography: {
      headingFont: 'Bebas Neue',
      bodyFont: 'Inter',
      headingWeight: 700,
      bodyWeight: 500,
    },

    appearance: {
      cardStyle: 'bordered',
      buttonStyle: 'solid',
      navbarStyle: 'dark',
      borderRadius: 'small',
    },
  },
),
  createPreset('velvet-luxury', 'Ciruela Speakeasy', 'Atmósfera profunda en tono ciruela con toques de oro rosa para restaurantes de lujo.', {
    appearanceMode: 'dark',
    colors: {
      background: '#120914', surface: '#1E1022', surfaceAlt: '#2C1832', primary: '#E8B4B8',
      secondary: '#4A2040', accent: '#F472B6', textPrimary: '#FDF2F8', textSecondary: '#E7C6D8',
      textMuted: '#A87B95', border: '#4A2346', price: '#E8B4B8', promo: '#FB7185',
    },
    typography: { headingFont: 'Cormorant Garamond', bodyFont: 'Montserrat', headingWeight: 700 },
    appearance: { cardStyle: 'flat', navbarStyle: 'dark', borderRadius: 'small' },
  }),
] as const;

export function normalizeHexColor(value: string): string {
  const normalized = value.trim().toUpperCase();
  return HEX_COLOR.test(normalized) ? normalized : "";
}

function rgbChannels(value: string): [number, number, number] | null {
  const normalized = normalizeHexColor(value);
  if (!normalized) return null;
  return [
    Number.parseInt(normalized.slice(1, 3), 16),
    Number.parseInt(normalized.slice(3, 5), 16),
    Number.parseInt(normalized.slice(5, 7), 16),
  ];
}

export function relativeLuminance(value: string): number {
  const channels = rgbChannels(value);
  if (!channels) return 0;
  const [red, green, blue] = channels.map((channel) => {
    const normalized = channel / 255;
    return normalized <= 0.04045
      ? normalized / 12.92
      : ((normalized + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * red + 0.7152 * green + 0.0722 * blue;
}

export function contrastRatio(first: string, second: string): number {
  const firstLuminance = relativeLuminance(first);
  const secondLuminance = relativeLuminance(second);
  const lighter = Math.max(firstLuminance, secondLuminance);
  const darker = Math.min(firstLuminance, secondLuminance);
  return (lighter + 0.05) / (darker + 0.05);
}

export function getReadableForeground(
  background: string,
): "#FFFFFF" | "#111111" {
  return contrastRatio(background, "#111111") >=
    contrastRatio(background, "#FFFFFF")
    ? "#111111"
    : "#FFFFFF";
}

function record(value: unknown): Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function enumValue<T extends readonly string[]>(
  value: unknown,
  values: T,
  fallback: T[number],
): T[number] {
  return typeof value === "string" && values.includes(value as T[number])
    ? (value as T[number])
    : fallback;
}

function weightValue(
  value: unknown,
  fallback: PublicMenuFontWeight,
): PublicMenuFontWeight {
  return typeof value === "number" &&
    (PUBLIC_MENU_FONT_WEIGHTS as readonly number[]).includes(value)
    ? (value as PublicMenuFontWeight)
    : fallback;
}

function colorValue(value: unknown, fallback: string): string {
  return typeof value === "string" && normalizeHexColor(value)
    ? normalizeHexColor(value)
    : fallback;
}

export function resolvePublicTheme(
  savedTheme: unknown,
  legacy?: LegacyMenuColors,
): PublicMenuTheme {
  const source = record(savedTheme);
  const colorsSource = record(source.colors);
  const typographySource = record(source.typography);
  const appearanceSource = record(source.appearance);
  const presetId = enumValue(
    source.presetId,
    PUBLIC_MENU_PRESET_IDS,
    DEFAULT_PUBLIC_THEME.presetId,
  );
  const preset =
    PUBLIC_MENU_PRESETS.find((item) => item.id === presetId)?.theme ??
    DEFAULT_PUBLIC_THEME;
  const legacyColors: Record<string, unknown> = savedTheme
    ? {}
    : {
        primary: legacy?.primary,
        secondary: legacy?.secondary,
        accent: legacy?.accent,
      };
  const colorSource: Record<string, unknown> = {
    ...legacyColors,
    ...colorsSource,
  };
  const colors = Object.fromEntries(
    Object.entries(preset.colors).map(([key, fallback]) => [
      key,
      colorValue(colorSource[key], fallback),
    ]),
  ) as unknown as PublicMenuTheme["colors"];
  colors.primaryForeground = colorValue(
    colorSource.primaryForeground,
    getReadableForeground(colors.primary),
  );
  colors.secondaryForeground = colorValue(
    colorSource.secondaryForeground,
    getReadableForeground(colors.secondary),
  );
  colors.accentForeground = colorValue(
    colorSource.accentForeground,
    getReadableForeground(colors.accent),
  );

  return {
    version: 1,
    presetId,
    layoutId: enumValue(
      source.layoutId,
      PUBLIC_MENU_LAYOUT_IDS,
      DEFAULT_PUBLIC_THEME.layoutId,
    ),
    layoutConfig: {},
    appearanceMode: enumValue(
      source.appearanceMode,
      ["light", "dark", "custom"] as const,
      preset.appearanceMode,
    ),
    colors,
    typography: {
      headingFont: enumValue(
        typographySource.headingFont,
        PUBLIC_MENU_FONT_FAMILIES,
        preset.typography.headingFont,
      ),
      bodyFont: enumValue(
        typographySource.bodyFont,
        PUBLIC_MENU_FONT_FAMILIES,
        preset.typography.bodyFont,
      ),
      headingWeight: weightValue(
        typographySource.headingWeight,
        preset.typography.headingWeight,
      ),
      bodyWeight: weightValue(
        typographySource.bodyWeight,
        preset.typography.bodyWeight,
      ),
      scale: enumValue(
        typographySource.scale,
        ["compact", "normal", "large"] as const,
        preset.typography.scale,
      ),
    },
    appearance: {
      borderRadius: enumValue(
        appearanceSource.borderRadius,
        ["small", "medium", "large"] as const,
        preset.appearance.borderRadius,
      ),
      cardStyle: enumValue(
        appearanceSource.cardStyle,
        ["flat", "bordered", "elevated"] as const,
        preset.appearance.cardStyle,
      ),
      buttonStyle: enumValue(
        appearanceSource.buttonStyle,
        ["solid", "soft", "outline"] as const,
        preset.appearance.buttonStyle,
      ),
      density: enumValue(
        appearanceSource.density,
        ["compact", "comfortable"] as const,
        preset.appearance.density,
      ),
      navbarStyle: enumValue(
        appearanceSource.navbarStyle,
        ["clear", "primary", "dark"] as const,
        preset.appearance.navbarStyle,
      ),
      cartStyle: enumValue(
        appearanceSource.cartStyle,
        ["surface", "surfaceAlt"] as const,
        preset.appearance.cartStyle,
      ),
      chatStyle: enumValue(
        appearanceSource.chatStyle,
        ["surface", "surfaceAlt", "tinted"] as const,
        preset.appearance.chatStyle,
      ),
      configuratorStyle: enumValue(
        appearanceSource.configuratorStyle,
        ["surface", "surfaceAlt"] as const,
        preset.appearance.configuratorStyle,
      ),
    },
  };
}

export function publicMenuFontStack(font: PublicMenuFontFamily): string {
  return `"${font}", ui-sans-serif, system-ui, sans-serif`;
}

export function publicThemeCssVariables(
  theme: PublicMenuTheme,
): Record<string, string> {
  const radius = { small: "0.5rem", medium: "0.875rem", large: "1.25rem" }[
    theme.appearance.borderRadius
  ];
  const typeScale = { compact: 0.92, normal: 1, large: 1.12 }[
    theme.typography.scale
  ];
  const chatStyle = theme.appearance.chatStyle;
  const chatBackground =
    chatStyle === "surface"
      ? "var(--menu-background)"
      : chatStyle === "surfaceAlt"
        ? "var(--menu-surface-alt)"
        : "color-mix(in srgb, var(--menu-primary) 10%, var(--menu-background))";
  const chatSurface =
    chatStyle === "tinted"
      ? "color-mix(in srgb, var(--menu-primary) 8%, var(--menu-surface))"
      : "var(--menu-surface)";
  const chatAssistantBubble =
    chatStyle === "surfaceAlt"
      ? "var(--menu-surface)"
      : chatStyle === "tinted"
        ? "color-mix(in srgb, var(--menu-primary) 12%, var(--menu-surface))"
        : "var(--menu-surface-alt)";
  return {
    "--menu-background": theme.colors.background,
    "--menu-surface": theme.colors.surface,
    "--menu-surface-alt": theme.colors.surfaceAlt,
    "--menu-primary": theme.colors.primary,
    "--menu-primary-foreground": theme.colors.primaryForeground,
    "--menu-secondary": theme.colors.secondary,
    "--menu-secondary-foreground": theme.colors.secondaryForeground,
    "--menu-accent": theme.colors.accent,
    "--menu-accent-foreground": theme.colors.accentForeground,
    "--menu-text-primary": theme.colors.textPrimary,
    "--menu-text-secondary": theme.colors.textSecondary,
    "--menu-text-muted": theme.colors.textMuted,
    "--menu-border": theme.colors.border,
    "--menu-price": theme.colors.price,
    "--menu-promo": theme.colors.promo,
    "--menu-promo-foreground": getReadableForeground(theme.colors.promo),
    "--menu-success": theme.colors.success,
    "--menu-danger": theme.colors.danger,
    "--menu-warning": theme.colors.warning,
    "--menu-chat-background": chatBackground,
    "--menu-chat-surface": chatSurface,
    "--menu-chat-assistant-bubble": chatAssistantBubble,
    "--menu-chat-assistant-foreground": "var(--menu-text-primary)",
    "--menu-chat-user-bubble": "var(--menu-primary)",
    "--menu-chat-user-foreground": "var(--menu-primary-foreground)",
    "--menu-chat-composer": chatSurface,
    "--menu-chat-online": "var(--menu-success)",
    "--menu-chat-online-foreground": getReadableForeground(theme.colors.success),
    "--menu-font-heading": publicMenuFontStack(theme.typography.headingFont),
    "--menu-font-body": publicMenuFontStack(theme.typography.bodyFont),
    "--menu-heading-weight": String(theme.typography.headingWeight),
    "--menu-body-weight": String(theme.typography.bodyWeight),
    "--menu-type-scale": String(typeScale),
    "--menu-radius": radius,
    "--menu-density-space":
      theme.appearance.density === "compact" ? "0.75rem" : "1rem",
    "--background": "var(--menu-background)",
    "--foreground": "var(--menu-text-primary)",
    "--surface": "var(--menu-surface)",
    "--surface-foreground": "var(--menu-text-primary)",
    "--surface-secondary": "var(--menu-surface-alt)",
    "--surface-secondary-foreground": "var(--menu-text-primary)",
    "--surface-tertiary": "var(--menu-surface-alt)",
    "--surface-tertiary-foreground": "var(--menu-text-primary)",
    "--muted": "var(--menu-text-muted)",
    "--border": "var(--menu-border)",
    "--separator": "var(--menu-border)",
    "--accent": "var(--menu-primary)",
    "--accent-foreground": "var(--menu-primary-foreground)",
    "--default": "var(--menu-secondary)",
    "--default-foreground": "var(--menu-secondary-foreground)",
    "--focus": "var(--menu-primary)",
    "--link": "var(--menu-primary)",
    "--success": "var(--menu-success)",
    "--success-foreground": getReadableForeground(theme.colors.success),
    "--danger": "var(--menu-danger)",
    "--danger-foreground": getReadableForeground(theme.colors.danger),
    "--warning": "var(--menu-warning)",
    "--warning-foreground": getReadableForeground(theme.colors.warning),
    "--field-background": "var(--menu-surface-alt)",
    "--field-foreground": "var(--menu-text-primary)",
    "--field-placeholder": "var(--menu-text-muted)",
    "--field-border": "var(--menu-border)",
    "--radius": radius,
    "--field-radius": radius,
    "--text-xs": `calc(0.75rem * ${typeScale})`,
    "--text-sm": `calc(0.875rem * ${typeScale})`,
    "--text-base": `calc(1rem * ${typeScale})`,
    "--text-lg": `calc(1.125rem * ${typeScale})`,
    "--text-xl": `calc(1.25rem * ${typeScale})`,
    "--text-2xl": `calc(1.5rem * ${typeScale})`,
    "--text-3xl": `calc(1.875rem * ${typeScale})`,
    "--text-4xl": `calc(2.25rem * ${typeScale})`,
    "--text-5xl": `calc(3rem * ${typeScale})`,
    "--text-6xl": `calc(3.75rem * ${typeScale})`,
  };
}
